import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildApiUrl, buildAssetUrl } from '../../../config/apiConfig';
import './HeroSection.css';
import { log, logError, warn } from '../../../utils/logger';

/** Tiempo que se muestra cada imagen antes de pasar a la siguiente (ms). */
const IMAGE_SLIDE_MS = 15000;

const parseSlidesFromHero = (hero) => {
  if (!hero) return [];
  let raw = hero.slides;

  if (Array.isArray(raw)) {
    // API / Sequelize a veces ya devuelve el array parseado
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      raw = null;
    } else {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        raw = null;
      }
    }
  } else if (raw != null && typeof raw === 'object') {
    raw = [raw];
  } else {
    raw = null;
  }

  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((s) => s && s.url)
      .map((s) => {
        const t = String(s.type || 'image').toLowerCase();
        const type = t === 'video' ? 'video' : 'image';
        return { type, url: String(s.url).trim() };
      })
      .filter((s) => s.url);
  }
  const mediaType = String(hero.media_type || 'image').toLowerCase();
  if (mediaType === 'video' && hero.video) {
    return [{ type: 'video', url: hero.video }];
  }
  if (hero.img) {
    return [{ type: 'image', url: hero.img }];
  }
  return [];
};

const buildLandingCarousel = (heroes) => {
  if (!heroes?.length) {
    return { slides: [], textbuttom: '', urlbuttom: '' };
  }
  const slides = heroes.flatMap((h) => parseSlidesFromHero(h));
  const lead = heroes[0];
  return {
    slides,
    textbuttom: lead?.textbuttom || '',
    urlbuttom: lead?.urlbuttom || '',
  };
};

const HeroSection = () => {
  const [carousel, setCarousel] = useState({ slides: [], textbuttom: '', urlbuttom: '' });
  const [loading, setLoading] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const videoRefs = useRef({});

  useEffect(() => {
    const fetchHero = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/heroes/public'), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Error al cargar hero');
        const data = await response.json();
        if (data.heroes && data.heroes.length > 0) {
          setCarousel(buildLandingCarousel(data.heroes));
        }
      } catch (err) {
        logError('[HeroSection] Error cargando hero:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHero();
  }, []);

  const { slides, textbuttom, urlbuttom } = carousel;
  const slidesKey = useMemo(() => slides.map((s) => `${s.type}:${s.url}`).join('|'), [slides]);

  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  useEffect(() => {
    setSlideIndex(0);
  }, [slidesKey]);

  const goNext = useCallback(() => {
    setSlideIndex((i) => {
      const list = slidesRef.current;
      if (list.length <= 1) return 0;
      return (i + 1) % list.length;
    });
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const id = window.setInterval(() => {
      setSlideIndex((idx) => {
        const list = slidesRef.current;
        if (!list.length || list.length <= 1) return 0;
        const safeIdx = ((idx % list.length) + list.length) % list.length;
        const cur = list[safeIdx];
        if (cur?.type === 'image') {
          return (safeIdx + 1) % list.length;
        }
        return safeIdx;
      });
    }, IMAGE_SLIDE_MS);

    return () => window.clearInterval(id);
  }, [slidesKey, slides.length]);

  useEffect(() => {
    slides.forEach((s, i) => {
      const el = videoRefs.current[i];
      if (!el || s.type !== 'video') return;
      if (i === slideIndex) {
        el.muted = true;
        const p = el.play();
        if (p !== undefined) {
          p.then(() => log('[HeroSection] Video activo')).catch((error) => {
            warn('[HeroSection] Autoplay bloqueado:', error);
            const onInteract = () => {
              el.play().catch(() => {});
              document.removeEventListener('click', onInteract);
              document.removeEventListener('touchstart', onInteract);
            };
            document.addEventListener('click', onInteract);
            document.addEventListener('touchstart', onInteract);
          });
        }
      } else {
        el.pause();
      }
    });
  }, [slideIndex, slides, slidesKey]);

  const handleButtonClick = () => {
    if (urlbuttom) {
      if (urlbuttom.startsWith('http')) {
        window.open(urlbuttom, '_blank');
      } else {
        window.location.href = urlbuttom;
      }
    }
  };

  const trackTransform =
    slides.length > 0 ? `translateX(-${(100 * slideIndex) / slides.length}%)` : 'none';

  if (loading) {
    return (
      <section className="hero hero--loading">
        <div className="hero__container">
          <div className="hero__loading">Cargando...</div>
        </div>
      </section>
    );
  }

  if (!slides.length) {
    return null;
  }

  return (
    <section className="hero">
      <div className="hero__media-root">
        <div
          className="hero__slider-viewport"
          style={{ '--hero-slide-count': slides.length }}
        >
          <div
            className="hero__slider-track"
            style={{
              width: `${slides.length * 100}%`,
              transform: trackTransform,
            }}
          >
            {slides.map((slide, i) => (
              <div
                key={`${slide.type}-${slide.url}-${i}`}
                className="hero__slider-slide"
              >
                {slide.type === 'video' ? (
                  <div className="hero__slider-video-wrap">
                    <video
                      ref={(el) => {
                        if (el) videoRefs.current[i] = el;
                        else delete videoRefs.current[i];
                      }}
                      className="hero__slider-video"
                      muted
                      playsInline
                      preload={i === slideIndex ? 'auto' : 'metadata'}
                      onEnded={() => {
                        setSlideIndex((current) => {
                          if (current !== i) return current;
                          const list = slidesRef.current;
                          if (!list.length || list.length <= 1) return 0;
                          return (current + 1) % list.length;
                        });
                      }}
                    >
                      <source src={buildAssetUrl(slide.url)} type="video/mp4" />
                      <source src={buildAssetUrl(slide.url)} type="video/webm" />
                    </video>
                  </div>
                ) : (
                  <div
                    className="hero__slider-image"
                    style={{
                      backgroundImage: slide.url ? `url(${buildAssetUrl(slide.url)})` : 'none',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {slides.length > 1 && (
          <div className="hero__dots" aria-hidden>
            {slides.map((_, i) => (
              <button
                key={`hero-slide-dot-${i}`}
                type="button"
                className={`hero__dot ${i === slideIndex ? 'hero__dot--active' : ''}`}
                onClick={() => setSlideIndex(i)}
                aria-label={`Ir al slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="hero__container">
        <div className="hero__content">
          {textbuttom && (
            <button type="button" className="hero__button" onClick={handleButtonClick}>
              {textbuttom}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
